import React, { useEffect, useState } from "react";
import {
  submitClosestFormOnEnterKeyDown,
  submitClosestFormOnDateInputKeyUp,
} from "../shared/utils/fieldCommitOnEnterKeyDown";
import { TIME_OPTIONS_15, getDueDateForApi, getDueDatePart, getDueTimePart } from "../shared/utils/dueDateTime";
import RepeatPatternPicker from "./RepeatPatternPicker";
import { normalizeRepeatRule } from "../shared/utils/recurrence";
import DateTimeFieldGroup from "./DateTimeFieldGroup";

/**
 * Reusable modal dialog for creating or editing a single task.
 *
 * Fields:
 * - Name
 * - Due date / time / priority (same row)
 * - Amount ($)
 * - Repeat
 *
 * This component is intentionally UI‑only. The parent is responsible for
 * calling APIs like createTask and for managing the open/close state.
 *
 * Props:
 * - open: boolean
 * - title?: string
 * - initialValues?: { name, due_date, amount, repeat, priority }
 * - defaultDueTime?: string (HH:mm) – default time when no initial due time (e.g. account working hours start)
 * - focusDueTime?: string (HH:mm) – when current value is blank, temporarily focus dropdown around this time on open
 * - dueTimeOptions?: { value: string, label: string }[] – defaults to TIME_OPTIONS_15; pass buildTimeOptionsForWorkingStart(wh) to start list at working hours
 * - onCancel?: () => void
 * - onSave: (payload) => void | Promise<void>
 */
function TaskAddModal({
  open,
  title = "Add task",
  initialValues,
  defaultDueTime,
  focusDueTime,
  dueTimeOptions,
  onCancel,
  onSave,
  showAutoScheduleToggle = false,
}) {
  const [name, setName] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [dueTime, setDueTime] = useState("");
  const [priority, setPriority] = useState("");
  const [autoScheduleEnabled, setAutoScheduleEnabled] = useState(true);
  const [amount, setAmount] = useState("");
  const [repeatRule, setRepeatRule] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const timeSelectOptions = dueTimeOptions ?? TIME_OPTIONS_15;

  // Reset form when opened or when initialValues change.
  useEffect(() => {
    if (!open) return;
    const iv = initialValues || {};
    setName(iv.name || "");
    const datePart = iv.due_date ? getDueDatePart(iv.due_date) : "";
    const timePart = iv.due_date ? getDueTimePart(iv.due_date) : "";
    if (datePart) {
      setDueDate(datePart);
    } else {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
        today.getDate()
      ).padStart(2, "0")}`;
      setDueDate(todayStr);
    }
    setDueTime(timePart || (defaultDueTime && String(defaultDueTime).trim()) || "");
    setPriority(iv.priority != null && String(iv.priority).trim() !== "" ? String(iv.priority).trim() : "Low");
    setAutoScheduleEnabled(true);
    setAmount(
      iv.amount != null && !Number.isNaN(Number(iv.amount))
        ? String(iv.amount)
        : ""
    );
    setRepeatRule(normalizeRepeatRule(iv.repeat_rule ?? iv.repeat, datePart));
    setSubmitting(false);
    setError("");
  }, [open, initialValues, defaultDueTime]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (!submitting && onCancel) onCancel();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, submitting, onCancel]);

  if (!open) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !submitting && onCancel) {
      onCancel();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!onSave || submitting) return;

    const trimmedName = String(name || "").trim();
    if (!trimmedName) {
      setError("Name is required.");
      return;
    }

    let amountNum = 0;
    if (amount != null && String(amount).trim() !== "") {
      const parsed = Number(amount);
      if (Number.isNaN(parsed)) {
        setError("Amount must be a number.");
        return;
      }
      amountNum = parsed;
    }

    const dueForApi = getDueDateForApi(dueDate, dueTime);

    const payload = {
      name: trimmedName,
      due_date: dueForApi || null,
      priority: priority != null ? String(priority).trim() : "",
      autoSchedule: autoScheduleEnabled,
      amount: amountNum,
      repeat_rule: repeatRule,
    };

    setSubmitting(true);
    setError("");
    try {
      const result = onSave(payload);
      if (result && typeof result.then === "function") {
        await result;
      }
    } catch (err) {
      const msg =
        err && typeof err.message === "string"
          ? err.message
          : "Failed to save task.";
      setError(msg);
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1100,
        padding: "12px",
        boxSizing: "border-box",
      }}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="task-add-modal-title"
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          maxWidth: "460px",
          width: "100%",
          boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
          padding: "18px 20px 16px",
          boxSizing: "border-box",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "10px",
          }}
        >
          <h3
            id="task-add-modal-title"
            style={{
              margin: 0,
              fontSize: "1rem",
              fontWeight: 600,
              color: "#212529",
            }}
          >
            {title}
          </h3>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {showAutoScheduleToggle && (
              <label
                title="When enabled, this task will be automatically scheduled into the earliest open slot."
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  cursor: submitting ? "not-allowed" : "pointer",
                  userSelect: "none",
                  margin: 0,
                  fontSize: 12,
                  color: "#57606a",
                }}
              >
                <input
                  type="checkbox"
                  checked={autoScheduleEnabled}
                  onChange={(e) => setAutoScheduleEnabled(e.target.checked)}
                  disabled={submitting}
                  style={{ margin: 0 }}
                  aria-label="Auto schedule newly created task"
                />
                <span>Auto schedule</span>
              </label>
            )}
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                disabled={submitting}
                aria-label="Close"
                style={{
                  border: "none",
                  background: "none",
                  cursor: submitting ? "not-allowed" : "pointer",
                  fontSize: "18px",
                  lineHeight: 1,
                  padding: "2px 4px",
                  color: "#6c757d",
                }}
              >
                ×
              </button>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <div>
              <label
                htmlFor="task-add-name"
                style={{ display: "block", fontSize: "12px", marginBottom: "4px", fontWeight: 500, textAlign: "left" }}
              >
                Name
              </label>
              <input
                id="task-add-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={submitting}
                required
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  fontSize: "13px",
                  borderRadius: "4px",
                  border: "1px solid #ced4da",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
              <div style={{ flex: 2, minWidth: 0 }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "8px",
                    marginBottom: "4px",
                  }}
                >
                  <label
                    htmlFor="task-add-due-date"
                    style={{ display: "block", fontSize: "12px", fontWeight: 500, textAlign: "left" }}
                  >
                    Due date
                  </label>
                  <label
                    htmlFor="task-add-due-time"
                    style={{ display: "block", fontSize: "12px", fontWeight: 500, textAlign: "left" }}
                  >
                    Time
                  </label>
                </div>
                <DateTimeFieldGroup
                  layout="inline"
                  dateId="task-add-due-date"
                  timeId="task-add-due-time"
                  date={dueDate}
                  onDateChange={setDueDate}
                  time={dueTime}
                  onTimeChange={setDueTime}
                  timeOptions={timeSelectOptions}
                  disabled={submitting}
                  onKeyDown={submitClosestFormOnEnterKeyDown}
                  onDateKeyUp={submitClosestFormOnDateInputKeyUp}
                  onTimeOpen={() => {
                    const focusValue = focusDueTime != null ? String(focusDueTime).trim() : "";
                    if (!dueTime && focusValue && timeSelectOptions.some((opt) => opt.value === focusValue)) {
                      setDueTime(focusValue);
                    }
                  }}
                  timeTitle="Time (optional)"
                  dateStyle={{
                    width: "100%",
                    padding: "6px 8px",
                    fontSize: "13px",
                    borderRadius: "4px",
                    border: "1px solid #ced4da",
                    boxSizing: "border-box",
                  }}
                  timeSelectStyle={{ width: "100%" }}
                  style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", alignItems: "end" }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label
                  htmlFor="task-add-priority"
                  style={{ display: "block", fontSize: "12px", marginBottom: "4px", fontWeight: 500, textAlign: "left" }}
                >
                  Priority
                </label>
                <select
                  id="task-add-priority"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  disabled={submitting}
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    fontSize: "13px",
                    borderRadius: "4px",
                    border: "1px solid #ced4da",
                    boxSizing: "border-box",
                  }}
                >
                  <option value="">—</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>
            </div>

            <div style={{ display: "flex", gap: "8px" }}>
              <div style={{ flex: 1 }}>
                <label
                  htmlFor="task-add-amount"
                  style={{ display: "block", fontSize: "12px", marginBottom: "4px", fontWeight: 500, textAlign: "left" }}
                >
                  Amount ($)
                </label>
                <input
                  id="task-add-amount"
                  type="text"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  disabled={submitting}
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    fontSize: "13px",
                    borderRadius: "4px",
                    border: "1px solid #ced4da",
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label
                  htmlFor="task-add-repeat"
                  style={{ display: "block", fontSize: "12px", marginBottom: "4px", fontWeight: 500, textAlign: "left" }}
                >
                  Repeat
                </label>
                <RepeatPatternPicker
                  id="task-add-repeat"
                  anchorDate={dueDate}
                  value={repeatRule}
                  onChange={setRepeatRule}
                  disabled={submitting}
                  dropdownPortal
                  selectStyle={{
                    width: "100%",
                    padding: "6px 8px",
                    fontSize: "13px",
                    borderRadius: "4px",
                    border: "1px solid #ced4da",
                  }}
                />
              </div>
            </div>

            {error && (
              <div
                style={{
                  fontSize: "13px",
                  color: "#b02a37",
                  marginTop: "4px",
                  padding: "6px 8px",
                  background: "#f8d7da",
                  border: "1px solid #f5c2c7",
                  borderRadius: "4px",
                }}
              >
                {error}
              </div>
            )}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "8px",
              marginTop: "14px",
            }}
          >
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                disabled={submitting}
                style={{
                  padding: "6px 12px",
                  fontSize: "12px",
                  borderRadius: "4px",
                  border: "1px solid #dee2e6",
                  background: "#fff",
                  color: "#495057",
                  cursor: submitting ? "not-allowed" : "pointer",
                }}
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: "6px 14px",
                fontSize: "12px",
                borderRadius: "4px",
                border: "none",
                background: "#0d6efd",
                color: "#fff",
                cursor: submitting ? "not-allowed" : "pointer",
                fontWeight: 500,
              }}
            >
              {submitting ? "Creating…" : "Create task"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TaskAddModal;

